import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import type { NodeProps } from "reactflow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NodeShell } from "@/features/lines/canvas/nodes";
import type { SandboxLaneNodeData } from "@/types/sim-sandbox.types";
import { useSandboxDraft } from "../hooks/useSandboxDraft";

// Node de fundo STAGE_LANE: divisão visual do estágio, reusando o visual dashed/muted do
// GroupNode via NodeShell (apps/web/src/features/lines/canvas/nodes/index.tsx) em vez de
// reimplementar — + header embutido (nome/contagem/reorder/remover/+máquina), portado de
// StageColumn.tsx pra dentro do canvas (`draggable:false`/`zIndex:-1` setados em lane-helpers.ts).
// `nodrag`/`nopan` nos controles: sem essas classes o pane (panOnDrag ligado) ou o sistema de
// drag capturam o mousedown.
export function SandboxLaneNode({ data }: NodeProps<SandboxLaneNodeData>) {
  const renameStage = useSandboxDraft((s) => s.renameStage);
  const reorderStage = useSandboxDraft((s) => s.reorderStage);
  const removeStage = useSandboxDraft((s) => s.removeStage);
  const addStage = useSandboxDraft((s) => s.addStage);
  const isFirst = data.index === 0;
  const isLast = data.index === data.totalStages - 1;

  if (data.isGhost) {
    return (
      <NodeShell className="flex h-full w-full items-start justify-center border-dashed border-muted-foreground/40 bg-transparent">
        <Button variant="ghost" size="sm" className="nodrag nopan" onClick={() => addStage()}>
          <Plus className="h-4 w-4" />
          Estágio
        </Button>
      </NodeShell>
    );
  }

  return (
    <NodeShell className="h-full w-full border-dashed border-muted-foreground/40 bg-muted/20 p-2">
      <div className="space-y-1.5 rounded-md border bg-card p-2 shadow-sm">
        {data.editable ? (
          <Input
            value={data.name}
            aria-label="Nome do estágio"
            onChange={(e) => renameStage(data.stageKey, e.target.value)}
            className="nodrag nopan h-7 text-xs"
          />
        ) : (
          <span className="block truncate text-xs font-medium">{data.name}</span>
        )}
        {/* Retorno IN LOCO da edição: a vazão estimada aparece onde o usuário
            mexe, em vez de num bloco de resultado que competia com a resposta da simulação.
            O gargalo é nomeado, nunca sinalizado só por cor. */}
        {data.throughputUnH != null ? (
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="font-mono font-medium">
              {Math.round(data.throughputUnH).toLocaleString("pt-BR")} un/h
            </span>
            {data.isBottleneck ? (
              <span className="rounded bg-amber-100 px-1 py-px text-[10px] font-medium text-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
                gargalo
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>
            {data.count} máquina{data.count === 1 ? "" : "s"}
          </span>
          {data.editable ? (
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="icon"
                className="nodrag nopan h-6 w-6"
                aria-label="Mover estágio à esquerda"
                disabled={isFirst}
                onClick={() => reorderStage(data.stageKey, data.index - 1)}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="nodrag nopan h-6 w-6"
                aria-label="Mover estágio à direita"
                disabled={isLast}
                onClick={() => reorderStage(data.stageKey, data.index + 1)}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="nodrag nopan h-6 w-6 text-destructive"
                aria-label="Remover estágio"
                disabled={data.totalStages <= 1}
                onClick={() => removeStage(data.stageKey)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : null}
        </div>
        {data.editable ? (
          <Button
            variant="outline"
            size="sm"
            className="nodrag nopan w-full"
            onClick={() => data.onAddMachine?.(data.stageKey)}
          >
            <Plus className="h-3.5 w-3.5" />
            Máquina
          </Button>
        ) : null}
      </div>
    </NodeShell>
  );
}
